import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { cn } from '~/utils/cn';
import { formatDateTime, formatRelative } from '~/utils/date';

import { useDeliveryRejections } from '../hooks/use-delivery-rejections';
import { humanizeCode } from '../utils/catalog-labels';
import { useCrossLinkSearch } from '../utils/nav-context';
import { BOUNDED_LIST_CLASS, DASH, formatCount, STICKY_HEAD_CLASS } from '../utils/run-format';

import { PUBLISH_REJECTIONS_ANCHOR } from './funnel-stage-link';
import { GateContentUnavailable } from './gate-content-unavailable';
import { LineageChip } from './lineage-chip';

interface Props {
  runId: string;
  /** Only read once DELIVERY has actually started (no point probing before then). */
  enabled: boolean;
  /** Extra classes for the section (the Delivery tab draws it as its own card). */
  className?: string;
}

/**
 * Finding 13 — the Delivery tab's per-product rejections (B1): a reason breakdown up top, then
 * one row per rejected product×destination (destination, error, when, attempt), "Load more" via
 * keyset paging. Renders nothing while the capability is unconfirmed-absent or there is nothing
 * to show — this is drill-down detail for a problem, not a section every run needs.
 */
export function DeliveryRejections({ runId, enabled, className }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const crossLinkSearch = useCrossLinkSearch();
  const rejections = useDeliveryRejections(runId, enabled);
  const pages = rejections.data?.pages.filter((p) => p !== null) ?? [];
  const rows = pages.flatMap((p) => p.items);
  // `reasonCounts` covers the WHOLE run (B1), same on every page — the first page's is enough.
  const reasonCounts = pages[0]?.reasonCounts ?? [];
  // The header total is the WHOLE run's rejection count (reasonCounts, one per product × destination,
  // so not a distinct-product count), never just the rows paged in so far — an older deploy with no
  // reasonCounts falls back to "N loaded" instead of lying.
  const totalRejections = reasonCounts.reduce((sum, r) => sum + r.count, 0);

  // Nothing to probe-fail loudly over: an older deploy, a run that never reached DELIVERY, or a
  // delivery with zero rejections all look the same here — quiet, not an error state.
  if (!enabled || (rejections.isSuccess && rows.length === 0 && !rejections.hasNextPage)) {
    return null;
  }

  return (
    <section
      id={PUBLISH_REJECTIONS_ANCHOR}
      aria-label={t('delivery.rejections.title')}
      className={cn('flex scroll-mt-4 flex-col gap-3', className)}
    >
      <h4 className="text-foreground text-sm font-semibold">
        {reasonCounts.length > 0
          ? t('delivery.rejections.titleCount', { count: totalRejections })
          : rows.length > 0
            ? t('delivery.rejections.titleCountLoaded', { count: rows.length })
            : t('delivery.rejections.title')}
      </h4>
      {reasonCounts.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {reasonCounts.map((r) => (
            <li key={r.errorCode}>
              <LineageChip tone="danger">
                {t(`delivery.rejections.reasons.${r.errorCode}`, {
                  defaultValue: humanizeCode(r.errorCode),
                })}{' '}
                <span className="tabular-nums">{formatCount(r.count)}</span>
              </LineageChip>
            </li>
          ))}
        </ul>
      ) : null}
      {rejections.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : rejections.isError ? (
        <GateContentUnavailable what={t('delivery.rejections.title')} />
      ) : rows.length === 0 ? null : (
        <>
          <div className="rounded-lg border">
            <Table containerClassName={BOUNDED_LIST_CLASS}>
              <TableHeader className={STICKY_HEAD_CLASS}>
                <TableRow>
                  <TableHead>{t('delivery.rejections.product')}</TableHead>
                  <TableHead className="w-px whitespace-nowrap">
                    {t('delivery.rejections.destination')}
                  </TableHead>
                  <TableHead>{t('delivery.rejections.error')}</TableHead>
                  <TableHead className="w-px whitespace-nowrap">
                    {t('delivery.rejections.when')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.productId}-${row.destination}`}>
                    <TableCell className="max-w-64 truncate">
                      {row.title ? (
                        <Link
                          to="/catalog-enrichment/products/$productId"
                          params={{ productId: row.productId }}
                          search={crossLinkSearch}
                          className="text-primary inline-flex cursor-pointer items-center gap-1 hover:underline"
                          title={row.title}
                        >
                          <span className="truncate">{row.title}</span>
                          <ArrowUpRight aria-hidden="true" className="size-3.5 shrink-0" />
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">
                          {t('delivery.rejections.removed')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{row.destination}</TableCell>
                    <TableCell className="text-muted-foreground max-w-96 text-sm break-words">
                      {row.errorCode
                        ? t(`delivery.rejections.reasons.${row.errorCode}`, {
                            defaultValue: humanizeCode(row.errorCode),
                          })
                        : (row.errorMessage ?? DASH)}
                      {row.errorCode && row.errorMessage ? (
                        <span className="block text-xs">{row.errorMessage}</span>
                      ) : null}
                      {row.attempt > 1 ? (
                        <span className="block text-xs">
                          {t('delivery.rejections.attempt', { attempt: row.attempt })}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground text-xs whitespace-nowrap"
                      title={formatDateTime(row.occurredAt)}
                    >
                      {formatRelative(row.occurredAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {rejections.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer self-start"
              onClick={() => {
                void rejections.fetchNextPage();
              }}
              disabled={rejections.isFetchingNextPage}
              isLoading={rejections.isFetchingNextPage}
            >
              {t('delivery.rejections.loadMore')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}
